import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Loader2 } from "lucide-react";
import axios from "axios";
import QuestionScreen from "@/components/QuestionScreen";
import ThemeToggle from "@/components/ThemeToggle";
import { useTheme } from "@/components/ThemeContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AuditFlow() {
  const { auditId } = useParams();
  const navigate = useNavigate();
  const { logos, logoStyle } = useTheme();
  const [questions, setQuestions] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [phase, setPhase] = useState("loading");
  const [error, setError] = useState(null);

  useEffect(() => {
    loadQuestions();
  }, []);

  const loadQuestions = async () => {
    try {
      const res = await axios.get(`${API}/questions`);
      setQuestions(res.data.questions);
      setPhase("questionnaire");
    } catch (err) {
      setError("Failed to load questions");
      console.error(err);
    }
  };

  const handleAnswer = (questionId, value) => {
    setAnswers(prev => ({ ...prev, [questionId]: value }));
    setTimeout(() => {
      if (currentIndex < questions.length - 1) {
        setCurrentIndex(prev => prev + 1);
      }
    }, 400);
  };

  const handlePrevious = () => {
    if (currentIndex > 0) setCurrentIndex(prev => prev - 1);
  };

  const handleGenerateReport = async () => {
    setPhase("generating");
    setError(null);
    try {
      const answersList = Object.entries(answers).map(([qId, value]) => ({
        question_id: qId, answer: value, score: 0
      }));
      await axios.put(`${API}/audits/${auditId}/answers`, { answers: answersList });
      // Kick off background report generation
      await axios.post(`${API}/audits/${auditId}/generate-report`);

      // Poll until report is ready (or fail after ~3 min)
      const maxAttempts = 90; // 90 * 2s = 180s max
      let attempt = 0;
      const poll = async () => {
        attempt += 1;
        try {
          const { data } = await axios.get(`${API}/audits/${auditId}`);
          if (data.status === "report_ready" && data.report) {
            navigate(`/report/${auditId}`);
            return;
          }
          if (attempt >= maxAttempts) {
            setError("Report is taking longer than expected. Please try again or contact us.");
            setPhase("questionnaire");
            setCurrentIndex(questions.length - 1);
            return;
          }
          setTimeout(poll, 2000);
        } catch (e) {
          if (attempt >= maxAttempts) {
            setError("Failed to generate report. Please try again.");
            setPhase("questionnaire");
            setCurrentIndex(questions.length - 1);
            return;
          }
          setTimeout(poll, 2000);
        }
      };
      setTimeout(poll, 2000);
    } catch (err) {
      console.error("Failed to generate report:", err);
      setError("Failed to generate report. Please try again.");
      setPhase("questionnaire");
      setCurrentIndex(questions.length - 1);
    }
  };

  if (phase === "loading" || !questions.length) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: 'var(--rhz-bg)' }} data-testid="audit-loading">
        <Loader2 className="h-8 w-8 text-[#DC0706] animate-spin" />
      </div>
    );
  }

  if (phase === "generating") {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center px-6" style={{ backgroundColor: 'var(--rhz-bg)' }} data-testid="generating-report">
        <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} className="text-center">
          <div className="relative mb-10">
            <img src={logos.icon} alt="RHZ" className="h-20 mx-auto animate-flame-pulse" style={logoStyle} />
          </div>
          <h2 className="font-['Bruno_Ace_SC'] text-xl sm:text-2xl uppercase tracking-[0.2em] mb-4" style={{ color: 'var(--rhz-text-primary)' }}>
            Generating Your Report
          </h2>
          <p className="font-['Albert_Sans'] text-sm mb-3" style={{ color: 'var(--rhz-text-secondary)' }}>
            Analysing your answers across all 6 business pillars...
          </p>
          <div
            className="max-w-sm mx-auto mb-8 px-4 py-3 rounded-sm flex items-start gap-2 text-left"
            style={{
              backgroundColor: 'rgba(220,7,6,0.08)',
              border: '1px solid rgba(220,7,6,0.25)',
            }}
            data-testid="do-not-close-warning"
          >
            <span className="font-['Bruno_Ace_SC'] text-[10px] tracking-[0.2em] uppercase text-[#DC0706] flex-shrink-0 mt-0.5">⚠</span>
            <p className="font-['Albert_Sans'] text-xs leading-relaxed" style={{ color: 'var(--rhz-text-primary)' }}>
              <span className="font-bold text-[#DC0706]">Please don't close this window.</span>{" "}
              This takes around 20–30 seconds. Closing now will lose your report.
            </p>
          </div>
          <div className="w-64 mx-auto h-1 rounded-full overflow-hidden" style={{ backgroundColor: 'var(--rhz-border)' }}>
            <motion.div className="h-full progress-flame rounded-full" initial={{ width: "0%" }} animate={{ width: "90%" }} transition={{ duration: 8, ease: "easeOut" }} />
          </div>
          <div className="mt-8 flex flex-col gap-2">
            {["Scoring pillar performance...", "Identifying key weaknesses...", "Benchmarking against industry norms...", "Compiling diagnostic findings..."].map((text, i) => (
              <motion.p key={i} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: i * 2 }}
                className="font-['Albert_Sans'] text-xs uppercase tracking-wider" style={{ color: 'var(--rhz-text-disabled)' }}>
                {text}
              </motion.p>
            ))}
          </div>
        </motion.div>
      </div>
    );
  }

  const currentQuestion = questions[currentIndex];
  const progress = ((currentIndex + 1) / questions.length) * 100;
  const allAnswered = questions.every(q => answers[q.id] !== undefined);
  const isLastQuestion = currentIndex === questions.length - 1;

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--rhz-bg)' }} data-testid="audit-questionnaire">
      {/* Top bar */}
      <div className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl" style={{ backgroundColor: 'var(--rhz-nav-bg)', borderBottom: '1px solid var(--rhz-border)' }}>
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-3 sm:py-4">
          <div className="flex items-center justify-between mb-3">
            <img src={logos.horizontal} alt="RHZ" className="h-6" style={logoStyle} />
            <div className="flex items-center gap-3">
              <ThemeToggle />
              <span className="font-['Albert_Sans'] text-xs uppercase tracking-wider" style={{ color: 'var(--rhz-text-secondary)' }}>
                {currentIndex + 1} / {questions.length}
              </span>
            </div>
          </div>
          <div className="h-1 rounded-full overflow-hidden" style={{ backgroundColor: 'var(--rhz-border)' }} data-testid="progress-bar">
            <motion.div className="h-full progress-flame rounded-full" animate={{ width: `${progress}%` }} transition={{ duration: 0.5, ease: "easeOut" }} />
          </div>
        </div>
      </div>

      {/* Question area */}
      <div className="pt-24 sm:pt-28 pb-28 sm:pb-32 px-4 sm:px-6">
        <div className="max-w-2xl mx-auto">
          {error && (
            <div className="mb-6 p-4 bg-[#DC0706]/10 border border-[#DC0706]/20 rounded-sm" data-testid="error-message">
              <p className="font-['Albert_Sans'] text-sm text-[#DC0706]">{error}</p>
            </div>
          )}
          <AnimatePresence mode="wait">
            <QuestionScreen
              key={currentQuestion.id}
              question={currentQuestion}
              selectedAnswer={answers[currentQuestion.id]}
              onAnswer={(value) => handleAnswer(currentQuestion.id, value)}
              questionNumber={currentIndex + 1}
              totalQuestions={questions.length}
            />
          </AnimatePresence>
        </div>
      </div>

      {/* Bottom controls */}
      <div className="fixed bottom-0 left-0 right-0 backdrop-blur-xl" style={{ backgroundColor: 'var(--rhz-nav-bg)', borderTop: '1px solid var(--rhz-border)' }}>
        <div className="max-w-2xl mx-auto px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between">
          <button
            data-testid="prev-question-btn"
            onClick={handlePrevious}
            disabled={currentIndex === 0}
            className="font-['Albert_Sans'] text-sm disabled:opacity-30 disabled:cursor-not-allowed transition-colors uppercase tracking-wider"
            style={{ color: 'var(--rhz-text-secondary)' }}
          >
            Previous
          </button>
          {isLastQuestion && allAnswered ? (
            <button
              data-testid="generate-report-btn"
              onClick={handleGenerateReport}
              className="bg-[#DC0706] text-white hover:bg-[#FE4D00] transition-all px-8 py-3 uppercase tracking-[0.2em] font-['Bruno_Ace_SC'] text-xs rounded-sm glow-red"
            >
              Generate Report
            </button>
          ) : (
            <button
              data-testid="next-question-btn"
              onClick={() => currentIndex < questions.length - 1 && setCurrentIndex(prev => prev + 1)}
              disabled={!answers[currentQuestion.id]}
              className="font-['Albert_Sans'] text-sm disabled:opacity-30 disabled:cursor-not-allowed transition-colors uppercase tracking-wider"
              style={{ color: 'var(--rhz-text-secondary)' }}
            >
              {answers[currentQuestion.id] ? "Next" : "Select an answer"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
